import React, { useCallback, memo } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import { intersection, isEmpty, get } from 'lodash';
import i18n from 'i18n';
import { MISSING_SUBSCRIPTION, NOT_AUTHENTICATED } from 'constants/visibility-types';
import Button from 'components/Button';
import PlayIcon from 'components/Icons/play';
import WatchlistIcon from 'components/Icons/watchlist';
import EpisodesIcon from 'components/Icons/episodes';
import TrailerIcon from 'components/Icons/trailer';
import { ActionButtonsWrapper } from './StyledComponents';

const playButtonId = 'details-play-button';
const watchlistButtonId = 'details-watchlist-button';
const episodesButtonId = 'details-episodes-button';
const trailerButtonId = 'details-trailer-button';

const StyledButton = styled(Button)`
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const WatchControls = (props) => {
  const { data, sectionName, isVisible, isOnPurchaseRequest, isExpandDescriptionButtonVisible } = props;
  const { onFocus, onItemEnter } = props;
  const hasSeasons = !!data.seasonIds;
  const isInFavorite = data.favorite;
  const trailerUrl = get(data, ['previewContentUrls', 'trailer']);
  const isAvailableToWatch = ![MISSING_SUBSCRIPTION, NOT_AUTHENTICATED].includes(data.visibilityDetails);

  const onEnter = useCallback(
    ({ id }) => {
      let type = null;
      if (id === playButtonId) type = 'play';
      if (id === watchlistButtonId) type = 'watchlist';
      if (id === episodesButtonId) type = 'episodes';
      if (id === trailerButtonId) type = 'trailer';
      onItemEnter({ type, id });
    },
    [onItemEnter],
  );

  let playButtonUI = null;

  const isSinglePurchase = !isEmpty(intersection(data.types, ['EST', 'TVOD'])) && data.price;

  if (isAvailableToWatch) {
    playButtonUI = [<PlayIcon key="play" />, ' ', i18n.t('detailsVOD.watch')];
  } else if (isSinglePurchase) {
    playButtonUI = i18n.t('detailsVOD.orderFrom', { price: data.price });
  } else {
    playButtonUI = i18n.t('detailsVOD.order');
  }

  return (
    <ActionButtonsWrapper
      id="ActionButtonsWrapper"
      isVisible={isVisible}
      sectionName={sectionName}
      config={{
        preventMouseNavigateWithin: true,
        leaveFor: {
          up: '@BACK_BUTTON',
          down: isExpandDescriptionButtonVisible ? '@EXPAND_DESCRIPTION_BUTTON' : '@COVER',
          right: '@COVER',
        },
      }}
      defaultSection
    >
      <StyledButton
        onFocus={onFocus}
        onEnter={onEnter}
        id={playButtonId}
        payload={{ id: playButtonId }}
        disabled={isOnPurchaseRequest}
        focusOnMouseEnter
      >
        {playButtonUI}
      </StyledButton>
      {hasSeasons ? (
        <Button
          onFocus={onFocus}
          onEnter={onEnter}
          id={episodesButtonId}
          payload={{ id: episodesButtonId }}
          focusOnMouseEnter
        >
          <EpisodesIcon />
          {i18n.t('detailsVOD.episodes')}
        </Button>
      ) : null}
      <Button
        onFocus={onFocus}
        onEnter={onEnter}
        id={watchlistButtonId}
        payload={{ id: watchlistButtonId }}
        isActive={isInFavorite}
        focusOnMouseEnter
      >
        <WatchlistIcon />
        {isInFavorite ? i18n.t('detailsVOD.watchlistRemove') : i18n.t('detailsVOD.watchlistAdd')}
      </Button>
      {trailerUrl ? (
        <Button
          onFocus={onFocus}
          onEnter={onEnter}
          id={trailerButtonId}
          payload={{ id: trailerButtonId }}
          focusOnMouseEnter
        >
          <TrailerIcon />
          {i18n.t('detailsVOD.trailer')}
        </Button>
      ) : null}
    </ActionButtonsWrapper>
  );
};

WatchControls.propTypes = {
  data: PropTypes.object.isRequired,
  sectionName: PropTypes.string.isRequired,
  onFocus: PropTypes.func.isRequired,
  onItemEnter: PropTypes.func.isRequired,
  isVisible: PropTypes.bool,
  isExpandDescriptionButtonVisible: PropTypes.bool,
};

export default memo(WatchControls);
